import React, { useMemo, useState } from "react";
import { ArrowRight, ArrowDown, Quote, Gauge, Scale, Flag, ArrowRightCircle, Eye, FileText } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const FACT_LABELS = {
  respiratory_symptom: "Respiratory symptom",
  symptom_duration: "Symptom duration",
  clinical_indication: "Clinical indication",
  management_impact: "Management impact",
};

// Submitted clinical documentation with verbatim source-span highlighting.
export function EvidenceNotePane({ note, spans, active, className }) {
  const segments = useMemo(() => {
    const sorted = [...(spans || [])].sort((a, b) => a.character_start - b.character_start);
    const out = [];
    let cursor = 0;
    sorted.forEach((s, i) => {
      if (s.character_start < cursor) return;
      if (s.character_start > cursor) out.push({ text: note.slice(cursor, s.character_start), mark: null });
      const isActive = active && s.character_start === active.character_start && s.character_end === active.character_end;
      out.push({ text: note.slice(s.character_start, s.character_end), mark: isActive ? "strong" : "soft", key: i });
      cursor = s.character_end;
    });
    if (cursor < note.length) out.push({ text: note.slice(cursor), mark: null });
    return out;
  }, [note, spans, active]);

  return (
    <div data-testid="evidence-note-pane" className={cn("rounded-md border border-slate-200 bg-white", className)}>
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/80 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        <FileText className="h-3.5 w-3.5 text-slate-400" /> Submitted Clinical Documentation
      </div>
      <p className="whitespace-pre-wrap p-3 text-sm leading-relaxed text-slate-800">
        {segments.length === 0 ? (note || "No documentation submitted.") : segments.map((seg, i) =>
          seg.mark ? (
            <mark key={i} className={seg.mark === "strong" ? "evidence-mark" : "evidence-mark-soft"}>{seg.text}</mark>
          ) : (
            <span key={i}>{seg.text}</span>
          ))}
      </p>
    </div>
  );
}

const ACCENTS = {
  requirement: "border-l-slate-400",
  evidence: "border-l-teal-500",
  evidenceEmpty: "border-l-amber-400",
  system: "border-l-slate-300",
  decisionOk: "border-l-emerald-500",
  decisionAction: "border-l-amber-500",
};

function ChainStep({ icon: Icon, label, accent = "system", children, step }) {
  return (
    <div className={cn("relative rounded-md border border-slate-200 border-l-2 bg-white px-3 py-2 shadow-sm", ACCENTS[accent])}>
      {step && (
        <span className="absolute -left-[13px] top-3 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-slate-900 font-display text-[9px] font-bold text-white shadow">{step}</span>
      )}
      <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

// The evidence-to-requirement chain (Source Record kept in a separate note pane).
export function EvidenceChain({ evaluation, active, setActive }) {
  const results = evaluation?.criterion_results || [];

  if (evaluation?.unsupported) {
    return (
      <div data-testid="evidence-assurance-graph"
        className="rounded-md border-l-4 border-l-indigo-500 border border-indigo-200 bg-indigo-50 p-6 text-sm text-indigo-900">
        <div className="mb-1 font-semibold">Unable to Evaluate — Applicable Policy Not Configured</div>
        RequiSure does not have an approved applicable policy configuration for this payer / test scope,
        so it cannot responsibly produce a readiness decision or an evidence chain. It does <strong>not</strong> borrow
        another payer's rule to manufacture an answer. This case is routed to policy / human review.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="evidence-assurance-graph">
      {results.map((r) => {
        const evidence = (r.evidence || []).filter((e) => e.span_verified);
        const best = evidence.length
          ? evidence.reduce((a, b) => (a.confidence >= b.confidence ? a : b)) : null;
        return (
          <div key={r.criterion_code}
            className="rounded-md border border-slate-200 bg-white shadow-sm"
            data-testid={`evidence-chain-${r.criterion_code}`}>
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/70 px-4 py-2.5">
              <div className="font-display text-sm font-semibold text-slate-900">
                Criterion {r.criterion_code}
              </div>
              <StatusBadge status={r.status} size="sm" />
            </div>
            {r.why_applies && (
              <div className="border-b border-slate-100 bg-white px-4 py-2 text-[11px] text-slate-500" data-testid={`why-applies-${r.criterion_code}`}>
                <span className="font-semibold uppercase tracking-wide text-slate-400">Why this applies · {r.classification}</span> — {r.why_applies}
              </div>
            )}

            <div className="evidence-rail space-y-2 p-4">
              <ChainStep icon={Scale} label="Coverage Requirement" accent="requirement" step="1">
                {r.description}
                {r.required_fact_type && (
                  <span className="ml-1 text-xs text-slate-500">
                    (expects: {FACT_LABELS[r.required_fact_type]})
                  </span>
                )}
              </ChainStep>
              <div className="flex justify-center"><ArrowDown className="h-4 w-4 text-slate-300" /></div>

              <ChainStep icon={Quote} label="Source Evidence (verbatim)" accent={evidence.length ? "evidence" : "evidenceEmpty"} step="2">
                {evidence.length ? (
                  <ul className="space-y-1.5">
                    {evidence.map((e, i) => (
                      <li key={i} className="flex items-start justify-between gap-2">
                        <span>
                          <span className="italic text-slate-700">“{e.source_quote}”</span>
                          <span className="ml-1 text-xs text-slate-500">
                            — {e.source_section || "note"} · conf {Math.round(e.confidence * 100)}%{e.ambiguous ? " · ambiguous" : ""}
                          </span>
                        </span>
                        <Button size="sm" variant="ghost"
                          className="h-6 shrink-0 px-2 text-teal-700 hover:bg-teal-100"
                          data-testid={`view-in-note-${r.criterion_code}-${i}`}
                          onClick={() => setActive && setActive(e)}>
                          <Eye className="mr-1 h-3 w-3" /> View in Note
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (r.fields_verified || r.fields_missing) ? (
                  <div className="space-y-0.5">
                    {(r.fields_verified || []).map((f) => (
                      <div key={f} className="text-emerald-700">✓ {f} — verified</div>
                    ))}
                    {(r.fields_missing || []).map((f) => (
                      <div key={f} className="text-amber-800">• {f} — not present</div>
                    ))}
                  </div>
                ) : (
                  <span className="font-medium text-amber-800">
                    Supporting documentation was not identified in the available record.
                  </span>
                )}
              </ChainStep>
              <div className="flex justify-center"><ArrowDown className="h-4 w-4 text-slate-300" /></div>

              <ChainStep icon={Flag} label="Rule Evaluation & Determination" accent="system" step="3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="rounded border border-slate-100 bg-slate-50/60 p-2">
                    <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"><Gauge className="h-3 w-3" /> AI Confidence</div>
                    <div className="mt-0.5 text-slate-700">{best ? `${Math.round(best.confidence * 100)}% (highest matching fact)` : "n/a"}</div>
                  </div>
                  <div className="rounded border border-slate-100 bg-slate-50/60 p-2">
                    <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"><Flag className="h-3 w-3" /> Deterministic Rule</div>
                    <div className="mt-0.5"><StatusBadge status={r.status} size="sm" /></div>
                  </div>
                </div>
              </ChainStep>
              <div className="flex justify-center"><ArrowDown className="h-4 w-4 text-slate-300" /></div>

              <ChainStep icon={ArrowRightCircle} label="Readiness Implication & Action"
                accent={r.status === "SATISFIED" ? "decisionOk" : "decisionAction"} step="4">
                <div>{r.reason}</div>
                {r.next_action && (
                  <div className="mt-1 flex items-start gap-1 text-xs font-medium text-slate-600">
                    <ArrowRight className="mt-0.5 h-3 w-3" /> {r.next_action}
                  </div>
                )}
              </ChainStep>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Backward-compatible standalone (chain + note) — self-manages active span.
export function EvidenceAssuranceGraph({ evaluation, note }) {
  const [active, setActive] = useState(null);
  const spans = (evaluation?.facts || []).filter((f) => f.span_verified);
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <EvidenceChain evaluation={evaluation} active={active} setActive={setActive} />
      <div className="lg:sticky lg:top-4 lg:self-start">
        <EvidenceNotePane note={note || ""} spans={spans} active={active} />
        <p className="mt-2 text-xs text-slate-500">
          Click <strong>View in Note</strong> on any evidence item to highlight the exact source
          text. Highlights only appear where a verbatim source span was verified — RequiSure never
          fabricates highlighted evidence.
        </p>
      </div>
    </div>
  );
}

export default EvidenceAssuranceGraph;
